/**
 * Small builders that keep the request fixtures short. Times are hours before
 * "now" so the data stays fresh whenever the demo is opened.
 */
import { getPerson } from '../../data/company';
import { getSupplier } from '../../data/suppliers';
import type { ContractId, PersonId, SupplierId } from '../../data/types';
import { approverName, hoursAgoMs, isoDaysFromNow } from './requests-helpers';
import type {
  ActivityActor,
  ActivityEntry,
  ApprovalStep,
  Approver,
  CheckId,
  CheckRef,
  CheckStatus,
  ProcurementRequest,
  RequestCheck,
  SuggestedSupplier,
} from './requests-types';

export function person(id: PersonId): Approver {
  return { kind: 'person', id };
}

export type NamedApprover = Extract<Approver, { kind: 'named' }>;

export function named(name: string, title: string): NamedApprover {
  return { kind: 'named', name, title };
}

export const NADIA = named(
  'Nadia Ferreira',
  'Finance business partner, Marketing',
);
export const RACHEL = named('Rachel Okonkwo', 'VP Marketing');
export const OWEN = named('Owen Park', 'Chief Information Security Officer');
export const RAY = named('Ray Delgado', 'Director, Facilities and Fleet');

/** Display name of a shared person, read from the data layer. */
export function personName(id: PersonId): string {
  return getPerson(id).name;
}

/** Short supplier name, read from the data layer. */
export function supplierShort(id: SupplierId): string {
  return getSupplier(id).shortName;
}

/** A supplier row for a supplier in the shared data, named from that data. */
export function knownSupplier(
  supplierId: SupplierId,
  role: string,
  contractId?: ContractId,
): SuggestedSupplier {
  const base = { supplierId, name: getSupplier(supplierId).name, role };
  return contractId ? { ...base, contractId } : base;
}

export const DAY = 24;

type CheckSeed = [CheckStatus, string] | [CheckStatus, string, CheckRef[]];

const CHECK_ORDER: CheckId[] = [
  'duplicate',
  'policy',
  'budget',
  'security',
  'legal',
  'supplier-risk',
];

/** Six checks in the standard order: duplicate, policy, budget, security, legal, supplier risk. */
export function checks(seeds: CheckSeed[]): RequestCheck[] {
  return CHECK_ORDER.map((id, i): RequestCheck => {
    const seed = seeds[i];
    if (!seed) return { id, status: 'na', summary: 'Not applicable.' };
    const [status, summary, refs] = seed;
    return refs ? { id, status, summary, refs } : { id, status, summary };
  });
}

interface ApprovalSeed {
  role: string;
  approver: Approver;
  /** Hours ago when approved. Omit for an open step. */
  doneH?: number;
  auto?: boolean;
  note?: string;
}

export function approvals(seeds: ApprovalSeed[]): ApprovalStep[] {
  return seeds.map((seed, i): ApprovalStep => {
    const step: ApprovalStep = {
      id: `step-${i + 1}`,
      role: seed.role,
      approver: seed.approver,
      status: seed.doneH === undefined ? 'open' : 'approved',
    };
    if (seed.doneH !== undefined) step.decidedAt = hoursAgoMs(seed.doneH);
    if (seed.auto) step.auto = true;
    if (seed.note) step.note = seed.note;
    return step;
  });
}

export interface ActivitySeed {
  /** Hours ago. */
  h: number;
  by: ActivityActor;
  text: string;
  detail?: string;
}

const AGENT: ActivityActor = { kind: 'agent' };

export function ag(h: number, text: string, detail?: string): ActivitySeed {
  return detail ? { h, by: AGENT, text, detail } : { h, by: AGENT, text };
}

export function by(
  h: number,
  actor: Approver,
  text: string,
  detail?: string,
): ActivitySeed {
  return detail ? { h, by: actor, text, detail } : { h, by: actor, text };
}

type ReceivedVia =
  | 'through web intake'
  | 'through Teams'
  | 'through Slack'
  | 'by email';

/** "Request received from <requester> through web intake". */
export function received(
  h: number,
  requesterId: PersonId,
  via: ReceivedVia,
  detail?: string,
): ActivitySeed {
  return ag(
    h,
    `Request received from ${personName(requesterId)} ${via}`,
    detail,
  );
}

/** "Budget owner approval: <name> approved". */
export function approvedBy(
  h: number,
  approver: Approver,
  role: string,
  detail?: string,
): ActivitySeed {
  return by(
    h,
    approver,
    `${role} approval: ${approverName(approver)} approved`,
    detail,
  );
}

/** "Routed to <name> for <role> approval". */
export function routedTo(
  h: number,
  approver: Approver,
  role: string,
): ActivitySeed {
  return ag(
    h,
    `Routed to ${approverName(approver)} for ${role.toLowerCase()} approval`,
  );
}

export function activity(
  requestId: string,
  seeds: ActivitySeed[],
): ActivityEntry[] {
  return seeds.map((seed, i): ActivityEntry => {
    const entry: ActivityEntry = {
      id: `${requestId}-act-${i + 1}`,
      at: hoursAgoMs(seed.h),
      actor: seed.by,
      text: seed.text,
    };
    if (seed.detail) entry.detail = seed.detail;
    return entry;
  });
}

type SeedRequest = Omit<
  ProcurementRequest,
  | 'createdAt'
  | 'activity'
  | 'skippedStages'
  | 'recurring'
  | 'channel'
  | 'neededBy'
> & {
  /** Hours since the request was created. */
  createdH: number;
  /** Days until needed, negative when past. Omit for none. */
  neededInDays?: number;
  activitySeeds: ActivitySeed[];
  skippedStages?: ProcurementRequest['skippedStages'];
  recurring?: boolean;
  channel?: ProcurementRequest['channel'];
};

/** Assemble a request from a compact seed. */
export function seedRequest(seed: SeedRequest): ProcurementRequest {
  const {
    createdH,
    neededInDays,
    activitySeeds,
    skippedStages,
    recurring,
    channel,
    ...rest
  } = seed;
  return {
    ...rest,
    neededBy: neededInDays === undefined ? null : isoDaysFromNow(neededInDays),
    createdAt: hoursAgoMs(createdH),
    skippedStages: skippedStages ?? [],
    recurring: recurring ?? false,
    channel: channel ?? 'web',
    activity: activity(rest.id, activitySeeds),
  };
}
